import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import {
  StoreUtil,
} from 'doctivity-shared/utils';

import {
  DynamicTable,
  AffiliatedIcon,
} from 'components';

import { ActivityLogsUtil } from 'utils';
import {
  queryOrganizations,
} from 'store/actions/organizationsActions';
import { favoritesByType } from 'store/actions/favoritesActions';
import { activityLogsByUserId } from 'store/actions/activityLogsActions';

// Empty stand-in so the table renders its "no data" state instead of crashing when a
// category has nothing in it yet (ActivityLogsUtil returns undefined for no history).
const NO_ROWS = { data: { rows: [], count: 0 } };

// Floor for the table area, so the dialog does not resize when you move between tabs --
// Favorite can be empty while All always has a full page. Sized to a full page of 10
// rows (header + filter row + 10 small rows + pagination) and deliberately rounded UP:
// any value at or above the tallest tab means every tab renders identically. If a full
// table leaves a visible gap underneath, lower this number.
const TABLE_MIN_HEIGHT = 520;

// this controls which columns are displayed and how they are looked up in data
const columns = [
  {
    label: '',
    sortable: false,
    showFilter: false,
    key: 'clients.id',
    type: 'bool',
    format: (_, row) =>{
      return row?.affiliated?.length > 0 ? <AffiliatedIcon /> : ''
    },
    style: { width: 16 },
  },
  {
    label: 'Name',
    key: 'name',
    fulltext: true,
  },
  {
    label: 'Type',
    key: 'facility_type',
  },
  {
    label: 'NPI',
    key: 'npi',
    type: 'npi',
  },
  {
    label: 'City',
    key: 'primary_location.city',
    fulltext: true,
  },
  {
    label: 'State',
    key: 'primary_location.state',
    style: { width: 50 },
    filterExact: true,
    autoCapitalize: true,
  },
];

// The Recent list comes back in last-viewed order from the activity log, so its columns
// must not offer sorting that the endpoint won't honour.
const recentColumns = columns.map((column) => ({ ...column, sortable: false }));

const styles = (theme) => ({
  filters: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  instructions: {
    fontSize: 14,
    fontWeight: 300,
    flex: 1,
    color: theme.palette.primary.main,
  },
  tabs: {
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    minHeight: `min(${TABLE_MIN_HEIGHT}px, 60vh)`,
  },
  selectedOrganizations: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  clearAllRow: {
    position: 'relative',
    bottom: -4,
    textAlign: 'right',
  },
  // Matches the "Clear All" link in the Specialties picker (SpecialtySearch).
  clearAllButton: {
    fontSize: '12px',
    fontFamily: 'Roboto',
    cursor: 'pointer',
    color: '#214E56',
    textTransform: 'none',
    fontWeight: '400',
    textDecoration: 'underline',
    padding: '3px 5px 0 5px',
    minWidth: 0,
    '&:hover': {
      background: 'none',
      textDecoration: 'underline',
    },
  },
});

/**
 * Organization picker.
 *
 * Four categories, mirroring the Organizations section's own pages and reusing the same
 * actions, so nothing new had to be added on the backend:
 *   recent     — ActivityLogs for this user (RecentOrganizationsPage)
 *   favorites  — favoritesByType (FavoriteOrganizationsPage)
 *   affiliated — a required `affiliated` include on this client (AffiliatedOrganizationsPage)
 *   all        — every organization, with a non-required affiliated include for the icon
 * "Suggested" has no organization equivalent — only Providers, Contacts and Locations
 * have a SMART_SUGGESTIONS report — so it is deliberately absent.
 *
 * In multi-select mode the chosen organizations are pinned as chips above the table so
 * they stay visible while you search other categories, each removable on its own.
 *
 * Selection can be controlled (pass `selectedRows`) or left to the dialog. Either way
 * `onMultiSelect` fires on every change, so existing callers keep working.
 */
class FindOrganizationDialog extends Component {
  constructor(props) {
    super(props);

    const organizationCategory = props.defaultCategory ?? 'affiliated';

    this.state = {
      organizationCategory,
      query: this.buildQuery(organizationCategory, props.clientId),
      // Only used when the caller does not control the selection itself.
      internalSelectedRows: props.selectedRows ? [...props.selectedRows] : [],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.clientId !== prevProps.clientId) {
      this.onCategoryChange(null, this.state.organizationCategory);
    }
  }

  /**
   * `primary_location` is ALWAYS include[0]. DynamicTable resolves a dotted column key
   * ('primary_location.city') to a path into query.include by array index, so moving it
   * would silently send the City/State filters to the wrong association.
   */
  buildQuery(category, clientId) {
    const include = [
      {
        association: 'primary_location',
        attributes: ['id', 'city', 'state'],
      },
    ];

    if (category === 'affiliated') {
      // A `where` with no explicit required:false makes this an INNER JOIN, which is
      // what limits the list to this client's affiliated organizations.
      include.push({
        association: 'affiliated',
        attributes: ['id'],
        through: { attributes: [] },
        where: { id: clientId },
      });
    } else if (category !== 'favorites') {
      // Just for the affiliated icon in the first column.
      include.push({
        association: 'affiliated',
        attributes: ['id'],
        required: false,
        duplicating: false,
        through: {
          attributes: [],
          where: { client_id: clientId },
        },
      });
    }
    // favorites deliberately gets no affiliated include: favoritesByType rebuilds the
    // organization include itself and would drop it anyway.

    return {
      where: {},
      include,
      limit: 10,
      offset: 0,
      // Recent is ordered by last viewed, server side.
      order: category === 'recent' ? undefined : [['name', 'ASC']],
    };
  }

  onQueryChange = (query) => {
    this.setState({ query }, this.fetchData.bind(this));
  };

  fetchData() {
    const { dispatch, clientId, userId } = this.props;
    const { organizationCategory, query } = this.state;
    const { where, include, order, limit, offset } = query;
    // Both of these graft `where` onto a nested association include; an empty object
    // there is pointless and the Favorites page strips it too.
    const typeWhere = where && Object.keys(where).length > 0 ? where : undefined;

    switch (organizationCategory) {
      case 'favorites':
        dispatch(
          favoritesByType(clientId, userId, 'Organizations', {
            where: typeWhere,
            include,
            order,
            limit,
            offset,
          }),
        );
        break;
      case 'recent':
        dispatch(
          activityLogsByUserId(userId, 'Organizations', clientId, {
            where: typeWhere,
            include,
            limit,
            offset,
          }),
        );
        break;
      default:
        dispatch(queryOrganizations(query));
        break;
    }
  }

  // The caller owns the selection when it passes selectedRows; otherwise we keep it.
  getSelectedRows() {
    return this.props.selectedRows ?? this.state.internalSelectedRows;
  }

  getCategoryData() {
    const { organizations, favorites, recents } = this.props;

    switch (this.state.organizationCategory) {
      // /Favorites returns FAVORITE records with the organization nested inside, so the
      // rows have to be unwrapped before the table sees them -- otherwise every column
      // reads undefined and, worse, row.id is the favorite's id rather than the
      // organization's, which would select the wrong thing. FavoriteOrganizationsPage
      // does the same unwrap. The store wrapper is kept so loading state still works.
      case 'favorites': {
        if (!favorites || !favorites.data) {
          return favorites ?? NO_ROWS;
        }
        const rows = (favorites.data.rows ?? [])
          .filter((row) => row.organization)
          .map((row) => row.organization);
        return {
          ...favorites,
          data: { ...favorites.data, rows },
        };
      }
      case 'recent':
        return recents ?? NO_ROWS;
      default:
        return organizations ?? NO_ROWS;
    }
  }

  render() {
    const {
      classes,
      open,
      onMultiSelect,
      onSaveMultiSelect,
    } = this.props;

    const { organizationCategory, query } = this.state;
    const selectedRows = this.getSelectedRows();
    const isMultiSelect = !!(onMultiSelect || onSaveMultiSelect);

    const tableProps = {};
    if (isMultiSelect) {
      tableProps.multiSelectedRows = selectedRows;
      tableProps.onMultiSelect = this.onTableMultiSelect;
    } else {
      tableProps.onRowClick = this.onRowClick;
    }

    return (
      <Dialog
        onClose={this.onCancel}
        aria-labelledby='simple-dialog-title'
        open={open}
        fullWidth
        maxWidth='lg'
        scroll='paper'
      >
        <DialogTitle>
          Find an Organization
        </DialogTitle>
        <DialogContent dividers>
          <Tabs
            variant='scrollable'
            className={classes.tabs}
            value={organizationCategory}
            onChange={this.onCategoryChange}
          >
            <Tab label='Recent' value='recent' disableRipple />
            <Tab label='Favorite' value='favorites' disableRipple />
            <Tab label='Affiliated' value='affiliated' disableRipple />
            <Tab label='All' value='all' disableRipple />
          </Tabs>
          {isMultiSelect && selectedRows.length > 0 && (
            <>
              <div className={classes.selectedOrganizations}>
                {selectedRows.map((organization) => (
                  <Chip
                    key={organization.id}
                    label={organization.name || `Organization ${organization.id}`}
                    variant='outlined'
                    onDelete={() => this.onDeleteOrganization(organization)}
                  />
                ))}
              </div>
              <div className={classes.clearAllRow}>
                <span
                  className={classes.clearAllButton}
                  onClick={this.onClearAll}
                >
                  Clear All
                </span>
              </div>
            </>
          )}
          <div className={classes.filters}>
            <Typography className={classes.instructions}>
              Filter and select the {isMultiSelect ? 'organizations' : 'organization'}
            </Typography>
          </div>
          <div className={classes.tableContainer}>
            <DynamicTable
              // Remount per tab. DynamicTable snapshots the query it is FIRST given as
              // its initialQuery and restores that on the clear-filters button -- with
              // one shared instance that snapshot stays the tab the dialog opened on,
              // so clearing from any other tab restored the Affiliated query (and its
              // required affiliated join) instead of the tab you were looking at.
              key={organizationCategory}
              columns={organizationCategory === 'recent' ? recentColumns : columns}
              query={query}
              data={this.getCategoryData()}
              noDataMessage={this.noDataMessage()}
              onQueryChange={this.onQueryChange}
              ContainerType={React.Fragment}
              {...tableProps}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.onCancel} color='primary'>
            Cancel
          </Button>
          {onSaveMultiSelect && (
            <Button
              onClick={onSaveMultiSelect}
              variant='contained'
              color='primary'
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  noDataMessage() {
    switch (this.state.organizationCategory) {
      case 'favorites':
        return 'No favorite organizations yet.';
      case 'recent':
        return 'No recently viewed organizations.';
      case 'affiliated':
        return 'No affiliated organizations found.';
      default:
        return 'No organizations found.';
    }
  }

  onCategoryChange = (_event, newValue) => {
    // Rebuild the query from scratch: each category needs a different include tree, and
    // carrying filters across would leave a name filter applied to a list the user just
    // switched away from.
    this.setState(
      {
        organizationCategory: newValue,
        query: this.buildQuery(newValue, this.props.clientId),
      },
      this.fetchData.bind(this),
    );
  };

  updateSelection(rows) {
    if (!this.props.selectedRows) {
      this.setState({ internalSelectedRows: rows });
    }
    if (this.props.onMultiSelect) {
      this.props.onMultiSelect(rows);
    }
  }

  // DynamicTable mutates the array it hands back (it pushes onto the same reference),
  // so copy before storing or passing it on.
  onTableMultiSelect = (rows) => {
    this.updateSelection([...rows]);
  };

  onClearAll = () => {
    this.updateSelection([]);
  };

  onDeleteOrganization = (organization) => {
    this.updateSelection(
      this.getSelectedRows().filter((row) => row.id !== organization.id),
    );
  };

  onCancel = () => {
    const {
      onSelect,
    } = this.props;

    if (onSelect) {
      onSelect(null);
    }
  };

  onRowClick = (row) => {
    const {
      onSelect,
    } = this.props;

    if (onSelect) {
      onSelect(row);
    }
  };
}

FindOrganizationDialog.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  clientId: PropTypes.number,
  userId: PropTypes.number,
  organizations: PropTypes.object.isRequired,
  favorites: PropTypes.object,
  recents: PropTypes.object,
  onSelect: PropTypes.func,
  onMultiSelect: PropTypes.func,
  onSaveMultiSelect: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  // 'recent' | 'favorites' | 'affiliated' | 'all'
  defaultCategory: PropTypes.string,
  open: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  const {
    organizations,
    favorites,
    activityLogs,
  } = state;

  const userId = state.user ? state.user.id : null;
  const clientId = state.app.selectedClient;

  return {
    userId,
    clientId,
    organizations: StoreUtil.get(organizations, StoreUtil.COMMON_TABLE),
    favorites: StoreUtil.get(
      favorites,
      'by_type',
      `${clientId}-${userId}-Organizations`,
    ),
    recents: ActivityLogsUtil.getOrganizationsFromLogs(activityLogs, userId),
  };
}

const styled = withStyles(styles)(FindOrganizationDialog);
const connected = connect(mapStateToProps)(styled);
export { connected as FindOrganizationDialog };
